import { RuleItem } from "../interfaces/RuleItem";
import { BonusTargetedFrom } from "../interfaces/BonusTargetedFrom";
import { BonusCalculationStatus, RuleModuleType } from "../constants/constants";
import { BonusDetail } from "../interfaces/BonusDetail";
import { BonusFromSource } from "../interfaces/BonusFromSource";
import { RuleModule } from "./RuleModule";
import {
  BonusSourceDetails,
  emptyBonusSourceDetails,
  buildBonusSourceDetails,
  CurrentBonuses,
} from "../interfaces/BonusSourceDetails";
import { BonusForTarget } from "../interfaces/BonusForTarget";
import { RankData } from "../interfaces/RankData";
import { StatProgressionSource } from "../interfaces/StatProgression";
import { StatDefinition } from "../interfaces/StatDefinition";
import { IXPModule } from "../interfaces/IXPModule";
import { Revision } from "../interfaces/Revision";
import { SpentXPDetails } from "../interfaces/SpentXPDetails";
import { UpgradeResultInfo } from "../interfaces/UpgradeResultInfo";
import { UpgradeResult } from "../interfaces/UpgradeResult";

export class CalculateBonuses {
  rankData: { [key: string]: RankData } = {};
  spentXPDetails: { [xpModuleKey: string]: SpentXPDetails[] } = {};
  upgradeInfo: { [key: string]: UpgradeResultInfo[] } = {};
  currentBonuses: CurrentBonuses = {};
  activeRuleItems: { [key: string]: RuleItem } = {};
  targetedBonuses: { [key: string]: BonusTargetedFrom[] } = {};
  statProgressions: {
    [key: string]: StatProgressionSource[];
  } = {};
  statDefinitions: { [key: string]: StatDefinition } = {};
  calculationStatus: { [key: string]: BonusCalculationStatus } = {};
  ruleModuleMap: { [key: string]: number } = {};
  xpModuleMap: { [key: string]: string } = {};

  constructor(revision: Revision) {
    this.rankData = JSON.parse(JSON.stringify(revision.rankData));
    this.spentXPDetails = JSON.parse(JSON.stringify(revision.spentXPDetails));
    Object.keys(this.spentXPDetails).forEach((moduleKey: string) => {
      this.spentXPDetails[moduleKey].forEach(
        (upgradeResults: SpentXPDetails) => {
          Object.keys(upgradeResults).forEach((targetKey: string) => {
            upgradeResults[targetKey].forEach(
              (upgradeResult: UpgradeResult) => {
                const upgradeResultInfo: UpgradeResultInfo = {
                  xpModule: moduleKey,
                  targetKey,
                  ...upgradeResult,
                };
                if (this.upgradeInfo[targetKey]) {
                  this.upgradeInfo[targetKey].push(upgradeResultInfo);
                } else {
                  this.upgradeInfo[targetKey] = [upgradeResultInfo];
                }
              }
            );
          });
        }
      );
    });
  }

  calculateBonuses(
    ruleModules: RuleModule[],
    xpModules: IXPModule[]
  ): CurrentBonuses {
    this.updateActiveRuleItemsForCharacter(ruleModules);
    this.addXPDefinitions(xpModules, ruleModules.length);
    this.mapTargetedBonuses();
    this.calculationStatus = {};
    this.currentBonuses = {};
    for (let key in this.activeRuleItems) {
      this.calculateBonus(key, ruleModules.concat(xpModules as any));
    }
    console.log("CalculateBonuses#currentBonuses", this.currentBonuses);
    console.log(
      "Rule Module Map",
      Object.keys(this.ruleModuleMap)
        .map((key) => {
          return [key, this.ruleModuleMap[key]];
        })
        .sort((a: any[], b: any[]) => {
          return a[1] - b[1];
        })
    );
    return this.currentBonuses;
  }

  updateActiveRuleItemsForCharacter(ruleModules: RuleModule[]) {
    this.activeRuleItems = {};
    ruleModules.forEach((ruleModule: RuleModule, i: number) => {
      for (let key in ruleModule.index) {
        if (
          key in this.rankData ||
          [
            RuleModuleType.RankOnly,
            RuleModuleType.ItemAndSource,
            RuleModuleType.ItemOnly,
            RuleModuleType.RankAndSource,
          ].includes(ruleModule.moduleType)
        ) {
          this.addRelevantRuleItem(ruleModule.getRuleItem(key), key, i);
        }
      }
      const stats = ruleModule.getStatDefinitions();
      stats.ruleItems.forEach((item, statIndex) => {
        this.statDefinitions[item.key] = stats.statDefinitions[statIndex];
        this.addRelevantRuleItem(item, item.key, i);
      });
    });
  }

  addXPDefinitions(xpModules: IXPModule[], moduleIndex: number): void {
    xpModules.forEach((xpModule, i) => {
      this.xpModuleMap[xpModule.key] = xpModule.name;
      this.addRelevantRuleItem(xpModule, xpModule.key, moduleIndex + i);
    });
  }

  addRelevantRuleItem(ruleItem: RuleItem, key: string, i: number) {
    this.ruleModuleMap[key] = i;
    this.activeRuleItems[key] = ruleItem;
  }

  mapTargetedBonuses() {
    this.targetedBonuses = {};
    for (let key in this.activeRuleItems) {
      const ruleItem: RuleItem = this.activeRuleItems[key];
      this.addTargetedBonus(ruleItem);
      this.addStatProgression(ruleItem);
    }
    console.log("statProgressions", this.statProgressions);
  }

  addTargetedBonus(ruleItem: RuleItem) {
    if (ruleItem.details.bonusForTarget) {
      ruleItem.details.bonusForTarget.forEach((bonus: BonusForTarget) => {
        const targetBonus: BonusTargetedFrom = {
          sourceName: ruleItem.name,
          sourceKey: ruleItem.key,
          multiplier: bonus.value,
          applyOnce: Boolean(bonus.applyOnce),
        };
        if (bonus.target in this.targetedBonuses) {
          this.targetedBonuses[bonus.target].push(targetBonus);
        } else {
          this.targetedBonuses[bonus.target] = [targetBonus];
        }
      });
    }
  }

  addStatProgression(ruleItem: RuleItem) {
    if (ruleItem.details.statProgression) {
      for (let key in ruleItem.details.statProgression) {
        const progression: StatProgressionSource = {
          source: ruleItem.key,
          progression: ruleItem.details.statProgression[key],
        };
        if (key in this.statProgressions) {
          this.statProgressions[key].push(progression);
        } else {
          this.statProgressions[key] = [progression];
        }
      }
    }
  }

  calculateBonus(key: string, ruleModules: RuleModule[]): BonusSourceDetails {
    if (!(key in this.activeRuleItems)) {
      return emptyBonusSourceDetails;
    }
    // Check if item has already been calculated
    if (key in this.calculationStatus) {
      switch (this.calculationStatus[key]) {
        case "done":
          return this.currentBonuses[key];
        case "started":
          const message = `Circular dependency detected for '${key}'!`;
          throw Error(message);
        default:
          throw Error(
            `Unknown bonus calculation state '${this.calculationStatus[key]}'.`
          );
      }
    }
    this.calculationStatus[key] = "started";

    // Variables
    const ruleModule: RuleModule = ruleModules[this.ruleModuleMap[key]];

    // Determine rank relative to base rank whether the character has ranks recorded or not
    let rank: number =
      // TODO: statDefinition default be value instead of a rank
      key in this.statDefinitions
        ? this.statDefinitions[key].default
        : ruleModule.config.default;
    if (key in this.rankData) {
      rank = this.rankData[key].rank;
    }

    // Update rank based on upgrades from spent XP
    const rankIncreases: BonusDetail[] = [];
    if (key in this.upgradeInfo) {
      this.upgradeInfo[key].forEach((upgradeResultInfo: UpgradeResultInfo) => {
        // The multiplier has already been applied to this value
        rank += upgradeResultInfo.resultUpgrade.value;
        rankIncreases.push({
          sourceName: this.xpModuleMap[upgradeResultInfo.xpModule],
          sourceKey: upgradeResultInfo.xpModule,
          current: upgradeResultInfo.resultUpgrade.value,
          bonusDetailType: "rank-improvement",
        });
      });
    }

    // Make adjustments based on config
    // TODO: limit rank based on min and max if present
    let current = rank;
    if (!(key in this.statDefinitions)) {
      if (ruleModule.config.baseRank) {
        current -= ruleModule.config.baseRank;
      }
    }

    // Calculate the current stat progression (level/value) for the current key (using the current rank variable) by looking at the rank of the RuleItems that target it (could be none)
    // TODO: figure out what to do with ranks vs allocate vs dice
    // TODO: handle keys that are targeted that aren't just statDefinitions (show as being targeted or add a warning for admins or handle the complexity)
    const statProgressionSourceIndex = new Set();
    if (key in this.statProgressions) {
      this.statProgressions[key].forEach(
        (progression: StatProgressionSource) => {
          statProgressionSourceIndex.add(progression.source);
          if (progression.progression.startingBonus) {
            // TODO: average the starting bonuses if multiple items selected that contribute to a stat
            current = progression.progression.startingBonus;
          }
          const sourceRank: number = this.calculateBonus(
            progression.source,
            ruleModules
          ).rank;
          for (
            let i = 0;
            i <=
            sourceRank -
              (this.statDefinitions[key].progression?.skipFirstLevel ? 2 : 1);
            i++
          ) {
            if (i < progression.progression.levels.length) {
              current += progression.progression.levels[i];
            } else {
              current +=
                this.statDefinitions[key].progression?.defaultMultiplier || 0;
            }
          }
        }
      );
    }

    // If this key has a stat definition, find current value based on level
    if (key in this.statDefinitions) {
      ruleModule.getKeys().forEach((sourceKey: string) => {
        if (!statProgressionSourceIndex.has(sourceKey)) {
          const sourceRank: number = this.calculateBonus(sourceKey, ruleModules)
            .rank;
          current +=
            sourceRank *
            (this.statDefinitions[key].progression?.defaultMultiplier || 0);
          // Make sure it is the default value if the level is zero
          if (
            this.statDefinitions[key].progression?.skipFirstLevel &&
            sourceRank > 0
          ) {
            current -=
              this.statDefinitions[key].progression?.defaultMultiplier || 0;
          }
          const statValueCap =
            ruleModule.config.selectOptions?.defaultMaxStat || 0;
          if (statValueCap && current > statValueCap) {
            current = statValueCap;
          }
        }
      });
    }

    // Calculate and collect bonuses from sources
    const ruleItem: RuleItem = this.activeRuleItems[key];
    const sources: BonusDetail[] = [];
    if (ruleItem.details.bonusCalculator) {
      ruleItem.details.bonusCalculator.sources.forEach(
        (source: BonusFromSource) => {
          const actualBonus: BonusDetail = this.calculateBonus(
            source.source,
            ruleModules
          ).actualBonus;
          sources.push(actualBonus);
          const bonus = source.multiplier
            ? actualBonus.current * source.multiplier
            : actualBonus.current;
          current += bonus;
        }
      );
    }

    // Calculate and collect bonuses targeted at this key
    let targeted: BonusDetail[] = [];
    if (key in this.targetedBonuses) {
      targeted = this.targetedBonuses[key].map(
        (targetedBonus: BonusTargetedFrom) => {
          const actualBonus: BonusDetail = this.calculateBonus(
            targetedBonus.sourceKey,
            ruleModules
          ).actualBonus;
          const bonus = targetedBonus.applyOnce
            ? targetedBonus.multiplier
            : actualBonus.current * targetedBonus.multiplier;
          const bonusDetailFromTarget: BonusDetail = {
            sourceName: targetedBonus.sourceName,
            sourceKey: targetedBonus.sourceKey,
            current: bonus,
            bonusDetailType: "bonus-targeted",
          };
          current += bonus;
          return bonusDetailFromTarget;
        }
      );
    }

    // TODO: calculate specified and default progressions

    const status: BonusCalculationStatus = "done";
    this.calculationStatus[key] = status;

    return (this.currentBonuses[key] = buildBonusSourceDetails(
      ruleItem,
      rank,
      current,
      sources,
      targeted,
      [],
      rankIncreases,
      ruleModule.moduleType,
      "bonus-from-source"
    ));
  }
}
